import { useCallback, useState } from "react"

import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationDeleteRunTransport } from "../routes/useConversationRunActionsTransport"
import { useInvalidateConversationsResultsTransport } from "../routes/useConversationsResultsTransportInvalidation"
import { getSelectedConversationsRunIds } from "../utils/conversationsTableState"

const log = logger("useConversationsBulkDelete")

interface UseConversationsBulkDeleteParams {
  allConversationsData: ConversationsTableResult[]
  runsToDelete: string[]
  selectedKeys: Set<string>
  setRunsToDelete: (runIds: string[]) => void
  setSelectedKeys: (selectedKeys: Set<string>) => void
}

export function useConversationsBulkDelete({
  allConversationsData,
  runsToDelete,
  selectedKeys,
  setRunsToDelete,
  setSelectedKeys,
}: UseConversationsBulkDeleteParams) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const deleteRunMutation = useConversationDeleteRunTransport()
  const invalidateConversationsResultsTransport =
    useInvalidateConversationsResultsTransport()
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), [])
  const openBulkDelete = useCallback(() => {
    if (selectedKeys.size === 0) {
      return
    }
    setRunsToDelete(
      getSelectedConversationsRunIds(allConversationsData, selectedKeys),
    )
    setIsDeleteModalOpen(true)
  }, [allConversationsData, selectedKeys, setRunsToDelete])
  const confirmBulkDelete = useCallback(async () => {
    setIsDeleting(true)
    const failedRunIds: string[] = []
    for (const runId of runsToDelete) {
      try {
        await deleteRunMutation.mutateAsync({ run_id: runId })
      } catch (error: unknown) {
        failedRunIds.push(runId)
        log.warn("Failed to delete run", {
          runId,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }
    setIsDeleting(false)
    closeDeleteModal()
    const [invalidationResult] = await Promise.allSettled([
      invalidateConversationsResultsTransport(),
    ])
    if (invalidationResult.status === "rejected") {
      log.warn(
        "Failed to invalidate uploaded conversation rows after deletion",
        {
          error:
            invalidationResult.reason instanceof Error
              ? invalidationResult.reason.message
              : String(invalidationResult.reason),
        },
      )
    }

    if (failedRunIds.length > 0) {
      toast.danger({
        title: `Failed to delete ${failedRunIds.length} of ${runsToDelete.length} items`,
      })
      setSelectedKeys(
        new Set(
          allConversationsData
            .filter(
              (row) =>
                failedRunIds.includes(row.run_id) && row.simulation_output_id,
            )
            .map((row) => row.simulation_output_id as string),
        ),
      )
    } else {
      toast.success({
        title: `Deleted ${runsToDelete.length} item${runsToDelete.length === 1 ? "" : "s"}`,
      })
      setSelectedKeys(new Set())
    }
    setRunsToDelete([])
  }, [
    allConversationsData,
    closeDeleteModal,
    deleteRunMutation,
    invalidateConversationsResultsTransport,
    runsToDelete,
    setRunsToDelete,
    setSelectedKeys,
  ])

  return {
    closeDeleteModal,
    confirmBulkDelete,
    isDeleteModalOpen,
    isDeleting,
    openBulkDelete,
  }
}
